<template>
  <div>
    <v-dialog v-model="dialog" max-width="100%" persistent scrollable>
      <v-card class="replicador-masivo-modal rounded-lg overflow-hidden">
        <!-- Header -->
        <v-card-title
          :class="['pa-3 px-4 d-flex justify-space-between align-center white--text', themeColor]"
        >
          <div class="d-flex align-center">
            <v-avatar color="white" size="32" class="mr-3 elevation-2">
              <v-icon :color="themeColor" size="20">{{
                mode === "edit" ? "mdi-pencil-box-multiple" : "mdi-content-copy"
              }}</v-icon>
            </v-avatar>
            <div>
              <span
                class="text-h6 font-weight-bold white--text d-block"
                style="line-height: 1.2"
              >
                {{
                  mode === "edit"
                    ? "Edición Masiva de Recetas"
                    : "Replicador Masivo de Recetas"
                }}
              </span>
              <span class="caption white--text text--lighten-4">
                {{
                  mode === "edit"
                    ? "Actualización en bloque e individual de recetas seleccionadas"
                    : "Duplicación inteligente e independiente de recetas seleccionadas"
                }}
              </span>
            </div>
          </div>
          <v-btn
            icon
            dark
            small
            @click="closeModal"
            :disabled="loadingExecution"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Stepper Header -->
        <v-sheet color="grey lighten-5" class="px-4 py-2">
          <div class="d-flex align-center justify-space-between flex-wrap">
            <div class="d-flex align-center">
              <v-tabs
                v-model="currentStep"
                :color="themeColor"
                :slider-color="themeColor"
                class="compact-stepper-tabs"
                dense
              >
                <v-tab :disabled="loadingExecution" class="font-weight-bold">
                  <v-avatar
                    size="22"
                    :color="currentStep === 0 ? themeColor : 'grey lighten-1'"
                    class="white--text mr-2 text-caption font-weight-bold"
                  >
                    1
                  </v-avatar>
                  {{
                    mode === "edit"
                      ? "Configurar y Editar"
                      : "Configurar y Replicar"
                  }}
                  <v-chip
                    x-small
                    class="ml-2"
                    :color="themeLightColor"
                    :text-color="themeColor"
                  >
                    {{ items.length }} receta(s)
                  </v-chip>
                </v-tab>

                <v-tab
                  :disabled="loadingExecution || !canProceedToReview"
                  class="font-weight-bold"
                >
                  <v-avatar
                    size="22"
                    :color="currentStep === 1 ? themeColor : 'grey lighten-1'"
                    class="white--text mr-2 text-caption font-weight-bold"
                  >
                    2
                  </v-avatar>
                  Revisión y Confirmación
                  <v-icon
                    v-if="!canProceedToReview"
                    x-small
                    color="orange darken-2"
                    class="ml-1"
                    title="Completa nombres y códigos válidos para avanzar"
                  >
                    mdi-alert-circle
                  </v-icon>
                </v-tab>
              </v-tabs>
            </div>

            <!-- Selector de Modalidad (Solo en Paso 1) -->
            <div v-if="currentStep === 0" class="d-flex align-center my-1">
              <div v-if="isSingleRecipe" class="d-flex align-center">
                <v-chip
                  small
                  :color="themeLightColor"
                  :class="`${themeTextClass} font-weight-bold`"
                >
                  <v-icon left x-small :color="themeColor">mdi-check-circle</v-icon>
                  Individual
                </v-chip>
              </div>

              <div v-else class="d-flex align-center">
                <span
                  class="text-caption font-weight-bold grey--text text--darken-2 mr-2"
                >
                  Modalidad de Trabajo:
                </span>
                <v-btn-toggle
                  v-model="editMode"
                  mandatory
                  dense
                  class="rounded-lg elevation-1"
                  :color="themeColor"
                >
                  <v-btn value="mixed" small class="text-capitalize px-3">
                    <v-icon left small :color="themeColor"
                      >mdi-tune-vertical</v-icon
                    >
                    Mixto
                  </v-btn>
                  <v-btn value="individual" small class="text-capitalize px-3">
                    <v-icon left small :color="themeColor"
                      >mdi-playlist-edit</v-icon
                    >
                    Individual
                  </v-btn>
                </v-btn-toggle>
              </div>
            </div>
          </div>
        </v-sheet>
        <v-divider></v-divider>

        <!-- Main Body -->
        <v-card-text
          class="pa-4 overflow-y-auto"
          style="max-height: 70vh"
        >
          <!-- =================== PASO 1: CONFIGURACIÓN =================== -->
          <div v-show="currentStep === 0">
            <!-- Explicación de la modalidad elegida -->
            <div
              class="mb-3 d-flex align-center justify-space-between flex-wrap"
            >
              <div class="d-flex align-center">
                <v-icon small :color="themeColor" class="mr-1">{{
                  modeIcon
                }}</v-icon>
                <span
                  class="text-subtitle-2 font-weight-bold"
                  :class="themeTextClass"
                >
                  {{ modeTitle }}:
                </span>
                <span class="text-caption grey--text text--darken-2 ml-1">
                  {{ modeDescription }}
                </span>
              </div>
            </div>

            <!-- CASO B: MODO "UNO POR UNO" (FICHA INDIVIDUAL) -->
            <div v-if="editMode === 'individual'">
              <!-- Barra superior de selector de vista en Individual -->
              <div class="d-flex align-center justify-space-between mb-3 px-1">
                <div class="d-flex align-center">
                  <span class="text-caption grey--text text--darken-2">
                    {{
                      isSingleRecipe
                        ? "Editando la receta seleccionada con sus 5 pestañas de control:"
                        : "Navega individualmente entre cada receta para personalizarla:"
                    }}
                  </span>
                </div>

                <div
                  v-if="!isSingleRecipe"
                  class="d-flex align-center"
                  style="gap: 6px"
                >
                  <span class="caption grey--text mr-1"
                    >Vista en Uno por Uno:</span
                  >
                  <v-btn-toggle
                    v-model="individualViewMode"
                    mandatory
                    dense
                    class="rounded elevation-1"
                  >
                    <v-btn
                      value="card"
                      x-small
                      class="px-2"
                      title="Ficha completa 1 a 1"
                    >
                      <v-icon x-small class="mr-1"
                        >mdi-card-text-outline</v-icon
                      >
                      Ficha
                    </v-btn>
                    <v-btn
                      value="table"
                      x-small
                      class="px-2"
                      title="Tabla comparativa"
                    >
                      <v-icon x-small class="mr-1">mdi-table</v-icon>
                      Tabla
                    </v-btn>
                  </v-btn-toggle>
                </div>
              </div>

              <!-- ================= SUB-VISTA: FICHA COMPLETA 1 A 1 ================= -->
              <div
                v-if="individualViewMode === 'card' && currentRecipeInFichaView"
              >
                <!-- Navegador de Recetas -->
                <v-card
                  v-if="!isSingleRecipe"
                  outlined
                  class="pa-2 px-3 mb-3 rounded-lg d-flex align-center justify-space-between grey lighten-5"
                >
                  <div class="d-flex align-center">
                    <v-btn
                      icon
                      small
                      :disabled="currentFichaIndex === 0"
                      @click="currentFichaIndex--"
                      title="Receta anterior"
                    >
                      <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>

                    <div style="min-width: 280px" class="mx-2">
                      <v-select
                        v-model="currentFichaIndex"
                        :items="recipeIndexItems"
                        item-value="index"
                        item-text="label"
                        dense
                        outlined
                        hide-details
                        background-color="white"
                        class="caption font-weight-bold"
                      ></v-select>
                    </div>

                    <v-btn
                      icon
                      small
                      :disabled="currentFichaIndex >= items.length - 1"
                      @click="currentFichaIndex++"
                      title="Siguiente receta"
                    >
                      <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>

                    <span class="caption grey--text ml-2">
                      {{ currentFichaIndex + 1 }} de {{ items.length }}
                    </span>
                  </div>

                  <div class="d-flex align-center">
                    <v-btn
                      text
                      x-small
                      color="red"
                      @click="removeItem(currentRecipeInFichaView)"
                      title="Quitar esta receta de la lista"
                    >
                      <v-icon left small>mdi-delete-outline</v-icon> Quitar
                      receta
                    </v-btn>
                  </div>
                </v-card>

                <!-- Tarjeta con 5 Pestañas de Edición Profunda -->
                <RecetaEditorTabs
                  :recipe="currentRecipeInFichaView"
                  :mode="mode"
                  :categories="categories"
                  :modulos="modulos"
                  :origenes="origenes"
                  :available-insumos="availableInsumosList"
                  :all-modificadores="allModificadoresList"
                  :available-tags="availableTags"
                  v-model="activeTabInFicha"
                  @notify="showNotify($event.message, $event.type)"
                />
              </div>
            </div>

            <!-- CASO C: MODO "MIXTO" (CAMPOS COMUNES EN LOTE) -->
            <div v-if="editMode === 'mixed'">
              <PanelAjustesLote
                :options="mixedOptions"
                :common-c-cs="mixedCommonCCs"
                :categories="categories"
                :modulos="modulos"
                :origenes="origenes"
                :channel-configs="channelConfigs"
                :modulos-map="modulosMap"
                :theme-color="themeColor"
                :theme-light-color="themeLightColor"
                :theme-text-class="themeTextClass"
                @apply="applyMixedRulesToItems"
              />
            </div>
          </div>

          <!-- =================== PASO 2: REVISIÓN Y CONFIRMACIÓN =================== -->
          <div v-show="currentStep === 1">
            <v-alert type="info" text dense class="mb-4">
              Revisa la lista final de recetas antes de guardar. Una vez
              confirmada la acción, el sistema enviará la operación masiva a la
              base de datos.
            </v-alert>

            <!-- Validaciones -->
            <v-alert v-if="hasEmptyNames" type="error" dense text class="mb-3">
              Hay recetas con el <strong>nombre vacío</strong>. Por favor
              escribe un nombre válido antes de confirmar.
            </v-alert>
            <v-alert v-if="hasEmptyCodes" type="error" dense text class="mb-3">
              Hay recetas con el <strong>código vacío</strong>. Por favor asigna
              un código válido antes de confirmar.
            </v-alert>
            <v-alert v-if="hasEmptyCategories" type="error" dense text class="mb-3">
              Hay recetas con la <strong>categoría vacía</strong>. Por favor asigna
              una categoría válida antes de confirmar.
            </v-alert>
            <v-alert
              v-if="hasEmptyDeptos"
              type="warning"
              dense
              text
              class="mb-3"
            >
              Hay recetas
              <strong>sin departamentos o almacenes asignados</strong>. Se
              recomienda asignar al menos uno para su correcta operación e
              inventario.
            </v-alert>
          </div>

          <!-- ================= TABLA DETALLADA COMPLETA ================= -->
          <div
            v-show="
              (currentStep === 0 &&
                (editMode === 'mixed' || individualViewMode === 'table')) ||
              currentStep === 1
            "
          >
            <div
              class="d-flex align-center justify-space-between mb-2 flex-wrap"
              style="gap: 8px"
            >
              <div class="d-flex align-center">
                <span
                  class="text-subtitle-2 font-weight-bold grey--text text--darken-3"
                >
                  {{
                    currentStep === 1
                      ? "Resumen Final de Recetas (" +
                        items.length +
                        "):"
                      : "Lista Detallada de Recetas (" + items.length + "):"
                  }}
                </span>
                <span class="caption grey--text ml-2">
                  {{
                    currentStep === 1
                      ? "(Verifica los datos antes de confirmar la operación masiva)"
                      : "(Cada fila muestra la versión original de referencia y la fila editable de réplica)"
                  }}
                </span>
              </div>
              <div style="max-width: 280px" class="flex-grow-1">
                <v-text-field
                  v-model="tableSearch"
                  prepend-inner-icon="mdi-magnify"
                  label="Buscar en la lista..."
                  dense
                  outlined
                  hide-details
                  clearable
                ></v-text-field>
              </div>
            </div>

            <v-card outlined class="rounded-lg overflow-hidden">
              <v-data-table
                :headers="activeTableHeaders"
                :items="filteredItems"
                :items-per-page="10"
                :footer-props="{
                  'items-per-page-options': [10, 25, 50, -1],
                  'items-per-page-text': 'Filas por página',
                }"
                dense
                class="interactive-edit-table"
                no-data-text="No se encontraron recetas con el filtro aplicado."
              >
                <template v-slot:item="{ item, index }">
                  <!-- Fila 1: Producto Original -->
                  <FilaRecetaOriginal
                    :item="item"
                    :index="index"
                    :current-step="currentStep"
                    :channel-configs="channelConfigs"
                    :categories-map="categoriesMap"
                    :modulos-map="modulosMap"
                    :origenes-map="origenesMap"
                    :modificadores-map="modificadoresMap"
                    :products-map="productsMap"
                    :modulos="modulos"
                    :origenes="origenes"
                    :theme-text-class="themeTextClass"
                    @remove="removeItem(item)"
                  />

                  <!-- Fila 2: Producto Replicado (Nuevo / Editado) -->
                  <tr
                    :key="'new-' + (item.originalId || index)"
                    class="row-nuevo-product white"
                  >
                    <!-- 1. Nombre -->
                    <td>
                      <div
                        v-if="currentStep === 0"
                        class="py-1 d-flex align-start"
                        style="min-width: 320px"
                      >
                        <v-chip
                          x-small
                          :color="themeColor"
                          dark
                          class="font-weight-bold mr-2 mt-2 flex-shrink-0"
                        >
                          {{ mode === "edit" ? "Editado" : "Nuevo" }}
                        </v-chip>
                        <v-textarea
                          v-model="item.Nombre"
                          dense
                          outlined
                          auto-grow
                          rows="1"
                          hide-details
                          class="resizable-textarea font-weight-medium flex-grow-1"
                          placeholder="Nombre de la receta"
                          :error="!item.Nombre || item.Nombre.trim() === ''"
                        ></v-textarea>
                      </div>
                      <div
                        v-else
                        class="py-2 d-flex align-center"
                        style="min-width: 320px"
                      >
                        <v-chip
                          x-small
                          :color="themeColor"
                          dark
                          class="font-weight-bold mr-2 flex-shrink-0"
                        >
                          {{ mode === "edit" ? "Editado" : "Nuevo" }}
                        </v-chip>
                        <span
                          class="font-weight-bold grey--text text--darken-4"
                          style="word-break: break-word"
                        >
                          {{ item.Nombre }}
                        </span>
                      </div>
                    </td>

                    <!-- 2. Código -->
                    <td>
                      <div
                        v-if="currentStep === 0"
                        class="py-1"
                        style="min-width: 155px"
                      >
                        <v-text-field
                          v-model="item.Codigo"
                          dense
                          outlined
                          hide-details
                          placeholder="Código"
                          :error="
                            !item.Codigo || String(item.Codigo).trim() === ''
                          "
                        ></v-text-field>
                      </div>
                      <div
                        v-else
                        class="py-1 font-family-monospace grey--text text--darken-4 font-weight-bold"
                        style="min-width: 155px"
                      >
                        <v-icon left x-small :color="themeColor"
                          >mdi-barcode</v-icon
                        >
                        {{ item.Codigo || "-" }}
                      </div>
                    </td>

                    <!-- 3. Categoría -->
                    <td>
                      <div
                        v-if="currentStep === 0"
                        class="py-1"
                        style="min-width: 235px"
                      >
                        <v-autocomplete
                          v-model="item.Categoria"
                          :items="categories"
                          item-value="id"
                          item-text="Nombre"
                          dense
                          outlined
                          hide-details
                          placeholder="Seleccionar categoría..."
                        ></v-autocomplete>
                      </div>
                      <div
                        v-else
                        class="py-1 text-truncate"
                        style="min-width: 235px"
                      >
                        <v-chip
                          x-small
                          :color="themeLightColor"
                          :class="`${themeTextClass} font-weight-bold`"
                        >
                          <v-icon left x-small :color="themeColor"
                            >mdi-shape</v-icon
                          >
                          {{ getCategoryNameById(item.Categoria) }}
                        </v-chip>
                      </div>
                    </td>

                    <!-- 4. Precio -->
                    <td>
                      <div
                        v-if="currentStep === 0"
                        class="py-1"
                        style="min-width: 195px"
                      >
                        <v-text-field
                          v-model.number="item.Precio"
                          type="number"
                          min="0"
                          step="0.5"
                          prefix="$"
                          :suffix="item.Moneda || ''"
                          dense
                          outlined
                          hide-details
                          class="font-weight-bold"
                        ></v-text-field>
                        <div
                          class="mt-1 d-flex align-center flex-wrap"
                          style="line-height: 1.2"
                        >
                          <template v-for="(priceInfo, pIdx) in [getPriceDiffInfo(item)]">
                            <v-chip
                              v-if="priceInfo.hasChanged"
                              :key="'chip-edit-' + pIdx"
                              x-small
                              :color="
                                priceInfo.diff > 0
                                  ? 'green lighten-5'
                                  : 'red lighten-5'
                              "
                              :class="
                                priceInfo.diff > 0
                                  ? 'green--text text--darken-3 font-weight-bold'
                                  : 'red--text text--darken-3 font-weight-bold'
                              "
                              class="mr-1"
                            >
                              {{ priceInfo.diff > 0 ? "+" : "" }}${{
                                priceInfo.diff.toFixed(2)
                              }}
                              ({{ priceInfo.percentDiff }}%)
                            </v-chip>
                            <span
                              v-else
                              :key="'nochange-edit-' + pIdx"
                              class="grey--text text-caption"
                              style="font-size: 11px"
                            >
                              (Sin cambios)
                            </span>
                          </template>
                        </div>
                      </div>
                      <div v-else class="py-1" style="min-width: 195px">
                        <span
                          class="font-weight-bold text-subtitle-2 grey--text text--darken-4"
                        >
                          ${{ $pgsHelper.formatNumber(item.Precio || 0) }}
                          {{ item.Moneda || "" }}
                        </span>
                        <div
                          class="mt-0.5 d-flex align-center flex-wrap"
                          style="line-height: 1.2"
                        >
                          <template v-for="(priceInfo, pIdx) in [getPriceDiffInfo(item)]">
                            <v-chip
                              v-if="priceInfo.hasChanged"
                              :key="'chip-read-' + pIdx"
                              x-small
                              :color="
                                priceInfo.diff > 0
                                  ? 'green lighten-5'
                                  : 'red lighten-5'
                              "
                              :class="
                                priceInfo.diff > 0
                                  ? 'green--text text--darken-3 font-weight-bold'
                                  : 'red--text text--darken-3 font-weight-bold'
                              "
                              class="mr-1"
                            >
                              {{ priceInfo.diff > 0 ? "+" : "" }}${{
                                priceInfo.diff.toFixed(2)
                              }}
                              ({{ priceInfo.percentDiff }}%)
                            </v-chip>
                            <span
                              v-else
                              :key="'nochange-read-' + pIdx"
                              class="grey--text text-caption"
                              style="font-size: 11px"
                            >
                              (Sin cambios)
                            </span>
                          </template>
                        </div>
                      </div>
                    </td>

                    <!-- 7. Vigencia -->
                    <td>
                      <div
                        v-if="currentStep === 0"
                        class="py-1"
                        style="min-width: 175px"
                      >
                        <v-menu
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          min-width="auto"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="item.vaunAux"
                              readonly
                              clearable
                              v-bind="attrs"
                              v-on="on"
                              dense
                              outlined
                              hide-details
                              placeholder="Sin vigencia"
                              prepend-inner-icon="mdi-calendar"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="item.vaunAux"
                            no-title
                            scrollable
                            :color="themeColor"
                          ></v-date-picker>
                        </v-menu>
                      </div>
                      <div
                        v-else
                        class="py-1 grey--text text--darken-3 text-caption"
                        style="min-width: 175px"
                      >
                        <v-icon x-small color="grey darken-1" class="mr-1"
                          >mdi-calendar-clock</v-icon
                        >
                        {{ item.vaunAux || "Sin vigencia" }}
                      </div>
                    </td>

                    <!-- 8. Deptos y Almacenes -->
                    <td>
                      <div
                        class="py-1"
                        style="min-width: 280px; max-width: 360px"
                      >
                        <template v-if="currentStep === 0">
                          <template
                            v-if="getRecipeDeptosRows(item).length === 0"
                          >
                            <v-btn
                              small
                              depressed
                              color="red lighten-5"
                              class="red--text text--darken-3 font-weight-bold px-2"
                              @click="openDeptosModal(item)"
                              title="Haz clic para vincular departamentos y almacenes"
                            >
                              <v-icon left small color="red darken-2">mdi-store-plus</v-icon>
                              Asignar Depto y Almacén
                            </v-btn>
                          </template>
                          <template v-else>
                            <div class="d-flex align-center justify-space-between mb-1">
                              <span :class="['text-caption font-weight-bold', themeTextClass]">
                                {{ getRecipeDeptosRows(item).length }} depto(s) vinculado(s)
                              </span>
                              <v-btn
                                x-small
                                depressed
                                :color="themeLightColor"
                                :class="`${themeTextClass} font-weight-bold px-2`"
                                @click="openDeptosModal(item)"
                                title="Clic para seleccionar o cambiar departamentos y almacenes"
                                style="height: 22px; font-size: 11px"
                              >
                                <v-icon x-small class="mr-1">mdi-pencil-outline</v-icon>
                                Editar
                              </v-btn>
                            </div>
                            <v-card
                              outlined
                              hover
                              class="pa-1.5 px-2 rounded-lg cursor-pointer"
                              style="background-color: #f8fafc; border-color: #cbd5e1;"
                              @click="openDeptosModal(item)"
                              title="Haz clic para seleccionar o cambiar departamentos y almacenes"
                            >
                              <div
                                v-for="d in getRecipeDeptosRows(item)"
                                :key="'tbl-new-d-' + d.depto"
                                class="mb-1"
                              >
                                <span
                                  :class="['font-weight-bold text-caption d-flex align-center', themeTextClass]"
                                >
                                  <v-icon small :color="themeColor" class="mr-1"
                                    >mdi-domain</v-icon
                                  >
                                  {{ d.deptoName }}
                                </span>
                                <div
                                  v-if="!d.origenes || d.origenes.length === 0"
                                  class="mt-0.5"
                                >
                                  <span
                                    class="caption orange--text font-weight-medium"
                                    style="font-size: 10px"
                                  >
                                    <v-icon x-small color="orange" class="mr-1"
                                      >mdi-warehouse</v-icon
                                    >
                                    Sin almacén
                                  </span>
                                </div>
                                <div
                                  v-else
                                  class="d-flex align-center flex-wrap mt-0.5"
                                  style="gap: 4px"
                                >
                                  <v-chip
                                    v-for="orig in d.origenes"
                                    :key="'new-orig-' + orig"
                                    x-small
                                    :color="themeLightColor"
                                    :class="`${themeTextClass} font-weight-medium`"
                                  >
                                    <v-icon left x-small :color="themeColor"
                                      >mdi-warehouse</v-icon
                                    >
                                    {{ getOrigenName(orig) }}
                                  </v-chip>
                                </div>
                              </div>
                            </v-card>
                          </template>
                        </template>
                        <template v-else>
                          <template
                            v-if="getRecipeDeptosRows(item).length === 0"
                          >
                            <span
                              class="caption red--text text--darken-2 font-weight-bold"
                            >
                              <v-icon x-small color="red"
                                >mdi-alert-circle</v-icon
                              >
                              Sin departamentos
                            </span>
                          </template>
                          <template v-else>
                            <div class="d-flex flex-column py-1">
                              <div
                                v-for="d in getRecipeDeptosRows(item)"
                                :key="'tbl-row-ro-' + d.depto"
                                class="mb-1"
                              >
                                <span
                                  :class="['font-weight-bold text-caption d-flex align-center', themeTextClass]"
                                >
                                  <v-icon small :color="themeColor" class="mr-1"
                                    >mdi-domain</v-icon
                                  >
                                  {{ d.deptoName }}
                                </span>
                                <div
                                  v-if="!d.origenes || d.origenes.length === 0"
                                >
                                  <span
                                    class="caption orange--text font-weight-medium"
                                    style="font-size: 10px"
                                  >
                                    <v-icon x-small color="orange" class="mr-1"
                                      >mdi-warehouse</v-icon
                                    >
                                    Sin almacén
                                  </span>
                                </div>
                                <div
                                  v-else
                                  class="d-flex align-center flex-wrap mt-0.5"
                                  style="gap: 4px"
                                >
                                  <v-chip
                                    v-for="orig in d.origenes"
                                    :key="'new-ro-orig-' + orig"
                                    x-small
                                    :color="themeLightColor"
                                    :class="`${themeTextClass} font-weight-medium`"
                                  >
                                    <v-icon left x-small :color="themeColor"
                                      >mdi-warehouse</v-icon
                                    >
                                    {{ getOrigenName(orig) }}
                                  </v-chip>
                                </div>
                              </div>
                            </div>
                          </template>
                        </template>
                      </div>
                    </td>

                    <!-- 9. Insumos -->
                    <CeldaResumenCambios
                      tipo="insumos"
                      :item="item"
                      :has-changes="hasInsumosChanged(item)"
                      :diff-list="getInsumosDiffList(item)"
                      :current-step="currentStep"
                      :theme-light-color="themeLightColor"
                      :theme-text-class="themeTextClass"
                      @edit="openInsumosModal(item)"
                    />

                    <!-- 10. Modificadores -->
                    <CeldaResumenCambios
                      tipo="modificadores"
                      :item="item"
                      :has-changes="hasModificadoresChanged(item)"
                      :diff-list="getModificadoresDiffList(item)"
                      :current-step="currentStep"
                      :theme-light-color="themeLightColor"
                      :theme-text-class="themeTextClass"
                      @edit="openModificadoresModal(item)"
                    />

                    <!-- 11. Canales de Venta -->
                    <td>
                      <div
                        class="py-1 d-flex align-center justify-center"
                        style="min-width: 215px"
                      >
                        <v-chip
                          v-for="ch in channelConfigs"
                          :key="ch.key"
                          x-small
                          :color="
                            !item[ch.key] ? ch.bgClass : 'grey lighten-3'
                          "
                          :class="[
                            !item[ch.key]
                              ? `${ch.textClass} font-weight-bold`
                              : 'grey--text text--darken-1',
                            currentStep === 0 ? 'cursor-pointer' : '',
                            'mx-1'
                          ]"
                          @click="
                            currentStep === 0
                              ? (item[ch.key] = !item[ch.key])
                              : null
                          "
                        >
                          <v-icon
                            left
                            x-small
                            :color="!item[ch.key] ? ch.activeColor : 'grey'"
                          >
                            {{ ch.icon }}
                          </v-icon>
                          {{ ch.label }}
                        </v-chip>
                      </div>
                    </td>

                    <!-- 12. Etiquetas -->
                    <td>
                      <div
                        v-if="currentStep === 0"
                        class="py-1"
                        style="min-width: 265px"
                      >
                        <v-combobox
                          v-model="item.tags"
                          multiple
                          small-chips
                          deletable-chips
                          dense
                          outlined
                          hide-details
                          placeholder="Añadir etiquetas..."
                        ></v-combobox>
                      </div>
                      <div v-else class="py-1" style="min-width: 265px">
                        <div
                          v-if="item.tags && item.tags.length > 0"
                          class="d-flex flex-wrap"
                          style="gap: 3px"
                        >
                          <v-chip
                            v-for="(t, tIdx) in item.tags"
                            :key="tIdx"
                            x-small
                            color="grey lighten-3"
                            class="grey--text text--darken-3 font-weight-medium"
                          >
                            {{ typeof t === "string" ? t : t.text || t.value }}
                          </v-chip>
                        </div>
                        <span v-else class="caption grey--text"
                          >Sin etiquetas</span
                        >
                      </div>
                    </td>

                    <!-- 13. Descripción / Notas -->
                    <td>
                      <div
                        v-if="currentStep === 0"
                        class="py-1"
                        style="min-width: 285px"
                      >
                        <v-textarea
                          v-model="item.dscr"
                          dense
                          outlined
                          auto-grow
                          rows="1"
                          hide-details
                          class="resizable-textarea"
                          placeholder="Descripción o notas..."
                        ></v-textarea>
                      </div>
                      <div
                        v-else
                        class="py-1"
                        style="min-width: 285px; max-width: 340px"
                      >
                        <span
                          class="caption grey--text text--darken-3"
                          style="word-break: break-word"
                        >
                          {{ item.dscr || "Sin descripción" }}
                        </span>
                      </div>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-card>
          </div>
        </v-card-text>

        <!-- Footer Actions -->
        <v-divider></v-divider>
        <v-card-actions
          class="pa-3 px-4 d-flex justify-space-between align-center grey lighten-5"
        >
          <div>
            <v-btn
              text
              color="grey darken-2"
              @click="closeModal"
              :disabled="loadingExecution"
            >
              Cancelar
            </v-btn>
          </div>

          <div class="d-flex align-center">
            <v-btn
              v-if="currentStep === 1"
              outlined
              color="grey darken-2"
              class="mr-2"
              @click="currentStep = 0"
              :disabled="loadingExecution"
            >
              <v-icon left small>mdi-chevron-left</v-icon>
              Regresar a Editar
            </v-btn>

            <v-btn
              v-if="currentStep === 0"
              :color="themeColor"
              dark
              depressed
              :disabled="!canProceedToReview || items.length === 0"
              @click="goToReviewStep"
            >
              Continuar a Revisión
              <v-icon right small>mdi-chevron-right</v-icon>
            </v-btn>

            <v-btn
              v-if="currentStep === 1"
              :color="themeColor"
              dark
              depressed
              :loading="loadingExecution"
              :disabled="loadingExecution || !canProceedToReview"
              @click="executeBulkReplication"
            >
              <v-icon left small>mdi-check</v-icon>
              {{
                mode === "edit"
                  ? `Actualizar ${items.length} Receta(s)`
                  : `Replicar ${items.length} Receta(s)`
              }}
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ================= MODAL DE EDICIÓN COMPLETA (5 PESTAÑAS COMPARTIDAS) ================= -->
    <v-dialog
      v-model="showFullEditDialog"
      max-width="950px"
      persistent
      scrollable
    >
      <v-card v-if="editingRecipeFull" class="rounded-lg">
        <v-card-title
          :class="['white--text py-3 d-flex justify-space-between align-center', themeColor]"
        >
          <div class="d-flex align-center">
            <v-icon left dark size="22">mdi-pencil-box-multiple</v-icon>
            <span class="text-subtitle-1 font-weight-bold">
              {{
                mode === "edit"
                  ? "Edición de Receta:"
                  : "Edición Integral de Receta:"
              }}
              {{ editingRecipeFull.Nombre }}
            </span>
          </div>
          <v-btn icon dark small @click="showFullEditDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text
          class="pa-4 overflow-y-auto"
          style="max-height: 70vh"
        >
          <RecetaEditorTabs
            :recipe="editingRecipeFull"
            :mode="mode"
            :categories="categories"
            :modulos="modulos"
            :origenes="origenes"
            :available-insumos="availableInsumosList"
            :all-modificadores="allModificadoresList"
            :available-tags="availableTags"
            :compact-modal="editMode === 'mixed'"
            v-model="activeTabInFullEdit"
            @notify="showNotify($event.message, $event.type)"
          />
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3 d-flex justify-end">
          <v-btn
            :color="themeColor"
            dark
            depressed
            @click="showFullEditDialog = false"
          >
            Listo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import RecetaEditorTabs from "./RecetaEditorTabs.vue";
import PanelAjustesLote from "./Replicador/PanelAjustesLote.vue";
import FilaRecetaOriginal from "./Replicador/FilaRecetaOriginal.vue";
import CeldaResumenCambios from "./Replicador/CeldaResumenCambios.vue";
import ReplicadorRecetas, {
  resolveId,
  buildModulosMap,
  buildOrigenesMap,
  buildModificadoresMap,
  getDeptoName as _getDeptoName,
  getOrigenName as _getOrigenName,
  getModificadorName as _getModificadorName,
  getRecipeDeptosRows as _getRecipeDeptosRows,
  getTotalAlmacenesCount as _getTotalAlmacenesCount,
  hasInsumosChanged,
  hasModificadoresChanged,
  getInsumosDiffList,
  getModificadoresDiffList,
  getDiffConfig,
} from "../../../clases/Restaurante/ReplicadorRecetas.js";

export default {
  name: "ReplicadorRecetasMasivoModal",
  components: {
    RecetaEditorTabs,
    PanelAjustesLote,
    FilaRecetaOriginal,
    CeldaResumenCambios,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    recetas: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    modulos: {
      type: Array,
      default: () => [],
    },
    origenes: {
      type: Array,
      default: () => [],
    },
    mode: {
      type: String,
      default: "replicate", // 'replicate' | 'edit'
    },
  },
  data() {
    return {
      currentStep: 0,
      editMode: "mixed", // 'individual' | 'mixed'
      tableSearch: "",
      mixedPanelOpen: 0,
      loadingExecution: false,
      executionProgress: {
        current: 0,
        total: 0,
        currentName: "",
        success: 0,
        failed: 0,
      },
      items: [],

      channelConfigs: [
        {
          key: "opdv",
          mixedKey: "pdv",
          label: "PDV",
          icon: "mdi-cash-register",
          activeColor: "green darken-2",
          textClass: "green--text text--darken-4",
          bgClass: "green lighten-5",
        },
        {
          key: "oweb",
          mixedKey: "web",
          label: "Web",
          icon: "mdi-web",
          iconOff: "mdi-web-off",
          activeColor: "blue darken-1",
          textClass: "blue--text text--darken-4",
          bgClass: "blue lighten-5",
        },
        {
          key: "ocmd",
          mixedKey: "cmd",
          label: "Móvil",
          icon: "mdi-tablet-cellphone",
          refIcon: "mdi-silverware",
          refIconOff: "mdi-silverware-clean",
          activeColor: "orange darken-3",
          refColor: "deep-orange",
          textClass: "orange--text text--darken-4",
          bgClass: "orange lighten-5",
        },
      ],

      // Columnas para v-data-table horizontal expandida
      tableHeaders: [
        {
          text: "#",
          value: "index",
          width: "50px",
          align: "center",
          sortable: false,
        },
        {
          text: "Producto / Receta *",
          value: "Nombre",
          width: "340px",
          sortable: false,
        },
        { text: "Código *", value: "Codigo", width: "170px", sortable: false },
        {
          text: "Categoría *",
          value: "Categoria",
          width: "250px",
          sortable: false,
        },
        {
          text: "Precio Venta *",
          value: "Precio",
          width: "210px",
          sortable: false,
        },
        {
          text: "Vigencia Precio",
          value: "Vaun",
          width: "190px",
          sortable: false,
        },
        {
          text: "Depto / Almacén",
          value: "Depto",
          width: "300px",
          sortable: false,
        },
        { text: "Insumos", value: "Insumos", width: "280px", sortable: false },
        {
          text: "Modificadores",
          value: "Modificadores",
          width: "270px",
          sortable: false,
        },
        {
          text: "Canales de Venta",
          value: "Canales",
          width: "230px",
          align: "center",
          sortable: false,
        },
        { text: "Etiquetas", value: "tags", width: "280px", sortable: false },
        {
          text: "Descripción / Notas",
          value: "dscr",
          width: "300px",
          sortable: false,
        },
        {
          text: "Acciones",
          value: "Acciones",
          width: "70px",
          align: "center",
          sortable: false,
        },
      ],

      // Vista en "Uno por Uno": 'card' (Ficha 1 a 1) o 'table' (Tabla Resumen)
      individualViewMode: "card",
      currentFichaIndex: 0,
      activeTabInFicha: 0,

      // Modal de Edición Integral
      showFullEditDialog: false,
      editingRecipeFull: null,
      activeTabInFullEdit: 0,

      // Inputs temporales para agregar Depto y Almacén Comunes
      mixedNewDepto: {
        depto: null,
        origen: null,
      },
      mixedCommonCCs: {},

      // Opciones para la edición masiva (nombres, precios, categoría, canales, deptos)
      mixedOptions: ReplicadorRecetas.getDefaultBatchOptions(),
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    isSingleRecipe() {
      return (
        (Array.isArray(this.recetas) && this.recetas.length === 1) ||
        (Array.isArray(this.items) && this.items.length === 1)
      );
    },
    executionPercent() {
      if (!this.executionProgress.total) return 0;
      return Math.round(
        (this.executionProgress.current / this.executionProgress.total) * 100,
      );
    },
    filteredItems() {
      if (!this.tableSearch || this.tableSearch.trim() === "") {
        return this.items;
      }
      const q = this.tableSearch.toLowerCase().trim();
      return this.items.filter(
        (it) =>
          (it.Nombre && it.Nombre.toLowerCase().includes(q)) ||
          (it.Codigo && String(it.Codigo).toLowerCase().includes(q)) ||
          (it.originalNombre && it.originalNombre.toLowerCase().includes(q)) ||
          (it.originalCodigo &&
            String(it.originalCodigo).toLowerCase().includes(q)),
      );
    },
    activeTableHeaders() {
      if (this.currentStep === 1) {
        return this.tableHeaders.filter((h) => h.value !== "Acciones");
      }
      return this.tableHeaders;
    },
    currentRecipeInFichaView() {
      if (this.items.length === 0) return null;
      const idx = Math.max(
        0,
        Math.min(this.currentFichaIndex, this.items.length - 1),
      );
      return this.items[idx];
    },
    recipeIndexItems() {
      return this.items.map((it, idx) => ({
        index: idx,
        label: `${idx + 1}. ${it.Nombre || it.originalNombre || "Receta"} ($${
          it.Precio || 0
        })`,
      }));
    },
    hasEmptyNames() {
      return this.items.some(
        (it) => !it.Nombre || it.Nombre.trim() === "",
      );
    },
    hasEmptyCodes() {
      return this.items.some(
        (it) => !it.Codigo || String(it.Codigo).trim() === "",
      );
    },
    mixedCommonDeptosList() {
      if (!this.mixedCommonCCs || typeof this.mixedCommonCCs !== "object")
        return [];
      return Object.entries(this.mixedCommonCCs).map(([depto, origenes]) => ({
        depto,
        deptoName: this.getDeptoName(depto),
        origenes: Array.isArray(origenes)
          ? origenes
          : origenes
          ? [origenes]
          : [],
      }));
    },
    mixedPriceModeLabel() {
      const mode = this.mixedOptions.price?.type || "percent";
      if (mode === "percent") {
        const sign = Number(this.mixedOptions.price?.percentType) > 0 ? "+" : "-";
        return `Porcentual: ${sign}${this.mixedOptions.price?.percentValue || 0}%`;
      }
      if (mode === "amount") {
        const sign = Number(this.mixedOptions.price?.amountType) > 0 ? "+" : "-";
        return `Unidades: ${sign}$${this.mixedOptions.price?.amountValue || 0} MXN`;
      }
      if (mode === "fixed") {
        return `Fijo: $${(
          Number(this.mixedOptions.price?.fixedValue) || 0
        ).toFixed(2)} MXN`;
      }
      return "";
    },
    mixedPriceExplanation() {
      const mode = this.mixedOptions.price?.type || "percent";
      if (mode === "percent") {
        const isUp = Number(this.mixedOptions.price?.percentType) > 0;
        const val = Number(this.mixedOptions.price?.percentValue) || 0;
        const sampleBase = 100;
        const sampleResult = isUp
          ? (sampleBase * (1 + val / 100)).toFixed(2)
          : Math.max(0, sampleBase * (1 - val / 100)).toFixed(2);
        return isUp
          ? `Aumento de +${val}% sobre el precio base de cada receta (ej. $100.00 pasará a $${sampleResult}).`
          : `Reducción/Descuento de -${val}% sobre el precio base de cada receta (ej. $100.00 pasará a $${sampleResult}).`;
      }
      if (mode === "amount") {
        const isUp = Number(this.mixedOptions.price?.amountType) > 0;
        const val = Number(this.mixedOptions.price?.amountValue) || 0;
        const sampleBase = 100;
        const sampleResult = isUp
          ? (sampleBase + val).toFixed(2)
          : Math.max(0, sampleBase - val).toFixed(2);
        return isUp
          ? `Se sumarán +$${val.toFixed(
              2,
            )} MXN al precio original de cada receta (ej. $100.00 pasará a $${sampleResult} MXN).`
          : `Se restarán -$${val.toFixed(
              2,
            )} MXN al precio original de cada receta (ej. $100.00 pasará a $${sampleResult} MXN).`;
      }
      if (mode === "fixed") {
        const val = Number(this.mixedOptions.price?.fixedValue) || 0;
        return `Precio Fijo: Todas las recetas tendrán exactamente $${val.toFixed(
          2,
        )} MXN, sin importar su precio original.`;
      }
      return "";
    },
    mixedChannelsSummary() {
      const active = [];
      if (this.mixedOptions.channels?.pdv) active.push("PDV");
      if (this.mixedOptions.channels?.web) active.push("Web");
      if (this.mixedOptions.channels?.cmd) active.push("Móvil");
      return active.length > 0 ? active.join(", ") : "Ninguno activo";
    },
    hasEmptyCategories() {
      return this.items.some((it) => !it.Categoria);
    },
    hasEmptyDeptos() {
      return this.items.some(
        (it) => this.getRecipeDeptosRows(it).length === 0,
      );
    },
    canProceedToReview() {
      return (
        this.items.length > 0 &&
        !this.hasEmptyNames &&
        !this.hasEmptyCodes &&
        !this.hasEmptyCategories &&
        !this.hasEmptyDeptos
      );
    },

    // Mapas indexados O(1) para lookups inmediatos
    categoriesMap() {
      const map = new Map();
      (this.categories || []).forEach((c) => {
        const id = resolveId(c);
        if (id) map.set(id, c.Nombre || c.text || id);
      });
      return map;
    },
    modulosMap() {
      return buildModulosMap(this.modulos);
    },
    origenesMap() {
      return buildOrigenesMap(this.origenes);
    },
    modificadoresMap() {
      return buildModificadoresMap(this.allModificadoresList);
    },
    productsMap() {
      const map = new Map();
      const prod = this.$store.getters.getProductos || [];
      prod.forEach((p) => {
        const id = resolveId(p);
        if (id) {
          map.set(id, p);
        }
      });
      return map;
    },

    availableInsumosList() {
      const prod = this.$store.getters.getProductos || [];
      return prod
        .filter(
          (p) =>
            (p.Tipo === "AYB-Insumo" || p.Tipo === "AYB-ICompuesto") && !p.Dlat,
        )
        .map((p) => {
          const id = p.id || p.Id;
          const um = (p.Extra && p.Extra.AYB && p.Extra.AYB.UM) || "PZA";
          const tipoLabel =
            p.Tipo === "AYB-ICompuesto" ? "Subreceta" : "Insumo";
          return {
            id: id,
            Id: id,
            Nombre: p.Nombre || "",
            Tipo: p.Tipo || "AYB-Insumo",
            UM: um,
            Moneda: p.Moneda || null,
            Extra: p.Extra || null,
            Catalogo: p.Catalogo || [],
            label: `${p.Nombre || "Sin nombre"} (${um}) - ${tipoLabel}`,
          };
        });
    },
    allModificadoresList() {
      const list =
        this.$store.getters["dataLists/getModificadores"] ||
        this.$store.getters.getModificadores ||
        [];
      return list
        .filter((m) => !m.Dlat)
        .map((m) => {
          const id = m.Id || m.id;
          const deptoText = m.Departamento ? ` [${m.Departamento}]` : "";
          return {
            id: id,
            Id: id,
            Nombre: m.Nombre || "",
            Departamento: m.Departamento || "",
            Opciones: m.Opciones || [],
            label: `${m.Nombre || "Sin nombre"}${deptoText}`,
          };
        });
    },
    availableTags() {
      const prod = this.$store.getters.getProductos || [];
      const set = new Set();
      prod.forEach((p) => {
        if (Array.isArray(p.tags)) {
          p.tags.forEach((t) => {
            const str = typeof t === "string" ? t : t.text || t.value;
            if (str) set.add(str);
          });
        }
      });
      return Array.from(set);
    },
    modeTitle() {
      if (this.editMode === "individual") return "Individual";
      return "Mixto";
    },
    modeIcon() {
      if (this.editMode === "individual") return "mdi-playlist-edit";
      return "mdi-tune-vertical";
    },
    themeColor() {
      return this.mode === "edit" ? "teal darken-1" : "primary";
    },
    themeLightColor() {
      return this.mode === "edit" ? "teal lighten-5" : "blue lighten-5";
    },
    themeHexColor() {
      return this.mode === "edit" ? "#00897B" : "#1976D2";
    },
    themeTextClass() {
      return this.mode === "edit" ? "teal--text text--darken-1" : "primary--text";
    },
    modeDescription() {
      if (this.isSingleRecipe) {
        return "Edición de la receta seleccionada con control total sobre sus datos principales, departamentos y almacenes, insumos, modificadores y canales de venta.";
      }
      if (this.editMode === "individual") {
        return "Edita cada receta con control total sobre datos principales, departamentos y almacenes, insumos, modificadores y canales de venta.";
      }
      return "Define campos comunes en lote (categoría, depto o ajuste de precio) y personaliza los detalles en la tabla inferior.";
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.initData();
      }
    },
    recetas: {
      immediate: true,
      handler(val) {
        if (val && val.length === 1) {
          this.editMode = "individual";
        }
      },
    },
    editMode(val) {
      if (val === "mixed" && this.isSingleRecipe) {
        this.$nextTick(() => {
          this.editMode = "individual";
        });
      }
    },
  },
  mounted() {
    if (this.value) {
      this.initData();
    }
  },
  methods: {
    getNextAvailableCodigos(count) {
      return ReplicadorRecetas.getNextAvailableCodigos(count);
    },

    initData() {
      this.currentStep = 0;
      this.tableSearch = "";
      this.currentFichaIndex = 0;
      this.activeTabInFicha = 0;

      if (this.recetas && this.recetas.length === 1) {
        this.editMode = "individual";
      } else {
        this.editMode = "mixed";
      }

      this.items = ReplicadorRecetas.initializeItems(this.recetas, this.mode);

      // Preseleccionar categoría si todas comparten la misma
      if (this.items.length > 0 && this.items[0].Categoria) {
        const firstCat = this.items[0].Categoria;
        const allSameCat = this.items.every((it) => it.Categoria === firstCat);
        if (allSameCat) {
          this.mixedOptions.targetCategory = firstCat;
        }
      }

      // Pre-cargar departamentos comunes si todas las recetas seleccionadas comparten los mismos
      this.mixedCommonCCs = {};
      this.mixedNewDepto = { depto: null, origenes: [] };
      if (this.items.length > 0 && this.items[0].CCs) {
        const firstCCs = this.items[0].CCs;
        const firstDeptoKeys = Object.keys(firstCCs);
        if (firstDeptoKeys.length > 0) {
          const allShareDeptos = this.items.every((it) => {
            if (!it.CCs) return false;
            const itKeys = Object.keys(it.CCs);
            return (
              itKeys.length === firstDeptoKeys.length &&
              firstDeptoKeys.every((k) => itKeys.includes(k))
            );
          });
          if (allShareDeptos) {
            const preMap = {};
            firstDeptoKeys.forEach((k) => {
              preMap[k] = Array.isArray(firstCCs[k]) ? [...firstCCs[k]] : [];
            });
            this.mixedCommonCCs = preMap;
          }
        }
      }
      if (this.items.length > 0) {
        this.mixedOptions.fixedPriceValue = this.items[0].Precio || 100;
      }
    },

    removeItem(item) {
      const idx = this.items.indexOf(item);
      if (idx !== -1) {
        this.items.splice(idx, 1);
        if (this.currentFichaIndex >= this.items.length) {
          this.currentFichaIndex = Math.max(0, this.items.length - 1);
        }
        if (this.items.length === 0) {
          this.closeModal();
        }
      }
    },

    getDeptoName(deptoId) {
      return _getDeptoName(deptoId, this.modulosMap, this.modulos);
    },

    getOrigenName(origenId) {
      return _getOrigenName(origenId, this.origenesMap, this.origenes);
    },

    getModificadorName(id) {
      return _getModificadorName(id, this.modificadoresMap);
    },

    getModificadorOrigenName(orn) {
      return this.getOrigenName(orn);
    },

    getInsumoDisplayName(ins) {
      if (!ins) return "Sin nombre";
      if (ins.Nombre && String(ins.Nombre).trim() !== "") return ins.Nombre;
      const insId = resolveId(ins);
      if (insId && this.productsMap.has(insId)) {
        return this.productsMap.get(insId).Nombre || insId;
      }
      return insId || "Insumo";
    },

    getInsumoUM(ins) {
      if (!ins) return "PZA";
      if (ins.UM && String(ins.UM).trim() !== "") return ins.UM;
      if (ins.Extra && ins.Extra.AYB && ins.Extra.AYB.UM)
        return ins.Extra.AYB.UM;
      const insId = resolveId(ins);
      if (insId && this.productsMap.has(insId)) {
        const p = this.productsMap.get(insId);
        return (p.Extra && p.Extra.AYB && p.Extra.AYB.UM) || p.UM || "PZA";
      }
      return "PZA";
    },

    getModificadorOptionsText(id) {
      if (!id || !this.modificadoresMap.has(id)) return "";
      const m = this.modificadoresMap.get(id);
      if (Array.isArray(m.Opciones) && m.Opciones.length > 0) {
        return m.Opciones.map((op) => op.Nombre || op.nombre || op).join(", ");
      }
      return "";
    },

    hasInsumosChanged(item) {
      return hasInsumosChanged(item);
    },

    hasModificadoresChanged(item) {
      return hasModificadoresChanged(item);
    },

    getInsumosDiffList(item) {
      return getInsumosDiffList(item, this.productsMap);
    },

    getModificadoresDiffList(item) {
      return getModificadoresDiffList(
        item,
        this.modificadoresMap,
        this.origenesMap,
        this.origenes
      );
    },

    getDiffConfig(status, type = "insumo") {
      return getDiffConfig(status, type);
    },

    openDeptosModal(item) {
      // En modo mixto: Tab 0 = Deptos. En modo individual (5 tabs): Tab 1 = Deptos
      const tabIdx = this.editMode === "mixed" ? 0 : 1;
      this.openFullEditModal(item, tabIdx);
    },

    openInsumosModal(item) {
      // En modo mixto: Tab 1 = Insumos. En modo individual (5 tabs): Tab 2 = Insumos
      const tabIdx = this.editMode === "mixed" ? 1 : 2;
      this.openFullEditModal(item, tabIdx);
    },

    openModificadoresModal(item) {
      // En modo mixto: Tab 2 = Modificadores. En modo individual (5 tabs): Tab 3 = Modificadores
      const tabIdx = this.editMode === "mixed" ? 2 : 3;
      this.openFullEditModal(item, tabIdx);
    },

    openFullEditModal(item, tabIndex = 0) {
      this.editingRecipeFull = item;
      this.activeTabInFullEdit = tabIndex;
      this.showFullEditDialog = true;
    },

    addCommonDepto() {
      if (!this.mixedNewDepto.depto || !this.mixedNewDepto.origen) return;
      const dKey = String(this.mixedNewDepto.depto).trim();
      const origenes = [this.mixedNewDepto.origen];

      if (!this.mixedCommonCCs || typeof this.mixedCommonCCs !== "object") {
        this.mixedCommonCCs = {};
      }
      this.$set(this.mixedCommonCCs, dKey, origenes);
      this.mixedNewDepto = { depto: null, origen: null };
    },

    removeCommonDepto(deptoKey) {
      if (!this.mixedCommonCCs) return;
      this.$delete(this.mixedCommonCCs, deptoKey);
    },

    updateCommonDeptoOrigenes(deptoKey, newOrigenes) {
      if (!this.mixedCommonCCs || typeof this.mixedCommonCCs !== "object") {
        this.mixedCommonCCs = {};
      }
      const arr = Array.isArray(newOrigenes)
        ? [...newOrigenes]
        : newOrigenes
        ? [newOrigenes]
        : [];
      this.$set(this.mixedCommonCCs, deptoKey, arr);
    },

    getRecipeDeptosRows(recipe) {
      return _getRecipeDeptosRows(recipe, this.modulosMap, this.modulos);
    },

    getOriginalDeptosRows(item) {
      if (!item) return [];
      return Object.entries(item.originalCCs || {}).map(
        ([depto, origenes]) => ({
          depto,
          deptoName: this.getDeptoName(depto),
          origenes: Array.isArray(origenes)
            ? origenes
            : origenes
            ? [origenes]
            : [],
        }),
      );
    },

    getOriginalInsumos(item) {
      if (!item) return [];
      return Array.isArray(item.originalCatalogo) ? item.originalCatalogo : [];
    },

    getOriginalModificadores(item) {
      if (!item) return [];
      return Array.isArray(item.originalModificadores)
        ? item.originalModificadores
        : [];
    },

    getTotalAlmacenesCount(recipe) {
      return _getTotalAlmacenesCount(recipe, this.modulosMap, this.modulos);
    },

    getCategoryNameById(catId) {
      if (!catId) return "Sin categoría";
      return this.categoriesMap.get(catId) || catId;
    },

    getOriginalDeptosSummary(item) {
      const rows = this.getOriginalDeptosRows(item);
      if (rows.length === 0) return "Sin departamento";
      return rows
        .map((r) => {
          const oNames =
            r.origenes.length > 0
              ? r.origenes.map((o) => this.getOrigenName(o)).join(", ")
              : "Sin almacén";
          return `${r.deptoName} [${oNames}]`;
        })
        .join("; ");
    },


    showNotify(message, type = "success") {
      if (this.$toast) {
        if (type === "warning") this.$toast.warning(message);
        else if (type === "error") this.$toast.error(message);
        else if (type === "info") this.$toast.info(message);
        else if (type === "success") this.$toast.success(message);
        else if (typeof this.$toast[type] === "function") this.$toast[type](message);
        else if (typeof this.$toast.show === "function") this.$toast.show(message);
        else if (typeof this.$toast.info === "function") this.$toast.info(message);
      } else if (this.$alert) {
        this.$alert.alert(type, message);
      }
    },

    getPriceDiffInfo(item) {
      return ReplicadorRecetas.getPriceDiffInfo(item);
    },

    // Aplica los cambios masivos configurados a todas las recetas de la tabla
    applyMixedRulesToItems() {
      if (
        this.mixedOptions.depto?.enabled &&
        Object.keys(this.mixedCommonCCs || {}).length === 0
      ) {
        this.showNotify(
          "Configura y agrega al menos un departamento común antes de aplicar",
          "warning",
        );
        return;
      }
      ReplicadorRecetas.applyMixedRules(
        this.items,
        this.mixedOptions,
        this.mixedCommonCCs,
        this.$set,
      );
      this.showNotify(
        "Campos comunes aplicados a la lista de recetas",
        "success",
      );
    },

    goToReviewStep() {
      if (!this.canProceedToReview) {
        this.showNotify(
          "Verifica que todas las recetas tengan nombre, código, categoría y departamentos válidos",
          "warning"
        );
        return;
      }
      this.currentStep = 1;
    },

    closeModal() {
      this.items = [];
      this.currentStep = 0;
      this.dialog = false;
    },

    preparePayload(item) {
      return ReplicadorRecetas.prepareProductPayload(item, this.mode);
    },

    _showValidationErrors(invalidItems) {
      return this.$swal({
        title: "Recetas con datos incompletos",
        html: `
          <p class="text-body-2 text--secondary mb-2">
            Hay receta(s) que no cumplen con los requisitos mínimos para guardarse:
          </p>
          <div class="text-left red lighten-5 red--text text--darken-3 pa-2 rounded overflow-y-auto" style="border: 1px solid #fed7d7; max-height: 180px; font-size: 0.85rem;">
            ${invalidItems.join("<br>")}
          </div>
        `,
        icon: "warning",
        confirmButtonColor: this.themeHexColor,
        confirmButtonText: "Entendido",
      });
    },

    _showProgressDialog(actionTitle, totalPayloads) {
      this.$swal({
        title: `${actionTitle}ndo recetas...`,
        html: `
          <div class="text-body-2 text--secondary mb-3">
            Por favor, espera un momento. El sistema está procesando cada receta.
          </div>
          <div style="width: 100%; background: #e2e8f0; border-radius: 9999px; height: 14px; overflow: hidden; margin-bottom: 10px;">
            <div id="swal-replica-progress-bar" style="width: 0%; height: 100%; background: ${this.themeHexColor}; transition: width 0.25s ease; border-radius: 9999px;"></div>
          </div>
          <div class="d-flex justify-space-between font-weight-bold text-body-2">
            <span id="swal-replica-progress-text">0 de ${totalPayloads} completadas</span>
            <span id="swal-replica-progress-percent">0%</span>
          </div>
          <div id="swal-replica-current-item" class="text-left grey lighten-5 pa-2 rounded text-caption text-truncate mt-2" style="border: 1px solid #e2e8f0;">
            Iniciando proceso...
          </div>
        `,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
      });
    },

    _updateProgressUI(current, total, currentItemName, status = "running") {
      const progressBar = document.getElementById("swal-replica-progress-bar");
      const progressText = document.getElementById("swal-replica-progress-text");
      const progressPercent = document.getElementById(
        "swal-replica-progress-percent",
      );
      const currentItemEl = document.getElementById(
        "swal-replica-current-item",
      );

      const pct = Math.round((current / total) * 100);
      if (progressBar) progressBar.style.width = `${pct}%`;
      if (progressText) {
        progressText.textContent = `${current} de ${total} completada(s)`;
      }
      if (progressPercent) progressPercent.textContent = `${pct}%`;
      if (currentItemEl) {
        if (status === "running") {
          currentItemEl.innerHTML = `<strong>Procesando:</strong> ${currentItemName}`;
        } else if (status === "success") {
          currentItemEl.innerHTML = `<strong>Completada:</strong> ${currentItemName}`;
        } else if (status === "failed") {
          currentItemEl.innerHTML = `<strong>Error en:</strong> ${currentItemName}`;
        }
      }
    },

    _showResultDialog(
      actionTitle,
      isEdit,
      successCount,
      totalPayloads,
      failedCount,
      failedCalls,
    ) {
      const messageHTML = `
        <p>Se ${
          isEdit ? "actualizaron" : "crearon"
        } exitosamente <strong>${successCount}</strong> de <strong>${totalPayloads}</strong> receta(s).</p>
        ${
          failedCount > 0
            ? `
          <p class="red--text">Hubo <strong>${failedCount}</strong> receta(s) que no se pudieron procesar.</p>
          <details class="text-left mt-2 red lighten-5 pa-2 rounded">
            <summary class="font-weight-bold red--text text--darken-3 cursor-pointer">Ver detalle de errores</summary>
            <div class="overflow-y-auto mt-1" style="max-height: 140px;">
              ${failedCalls
                .map((err, i) => {
                  const errorText =
                    err.status === "rejected"
                      ? (err.reason && (err.reason.message || err.reason.error)) ||
                        "Error de conexión"
                      : (err.value && err.value.data && (err.value.data.error || err.value.data.message)) ||
                        "Error sin detalle del servidor";
                  return `<p class="my-1 text-caption"><strong>Error ${
                    i + 1
                  }:</strong> ${errorText}</p>`;
                })
                .join("")}
            </div>
          </details>
        `
            : ""
        }
      `;

      return this.$swal({
        title:
          failedCount === 0
            ? `¡${actionTitle}ón Exitosa!`
            : `${actionTitle}ón Parcial`,
        html: messageHTML,
        icon: failedCount === 0 ? "success" : "warning",
        confirmButtonColor: this.themeHexColor,
        confirmButtonText: "Aceptar",
        allowOutsideClick: false,
      });
    },

    async executeBulkReplication() {
      const itemsToCreate = this.items;
      if (itemsToCreate.length === 0) return;

      const isEdit = this.mode === "edit";
      const actionText = isEdit ? "actualizar" : "replicar";
      const actionTitle = isEdit ? "Actualizar" : "Replicar";

      // Validación pre-vuelo: asegurar que ninguna receta tenga datos incompletos
      const invalidItems = [];
      itemsToCreate.forEach((it, idx) => {
        const errors = [];
        if (!it.Nombre || it.Nombre.trim().length < 2) {
          errors.push("Nombre inválido (mínimo 2 caracteres)");
        }
        if (!it.Categoria) {
          errors.push("Falta categoría");
        }
        if (!isEdit && (!it.Codigo || String(it.Codigo).trim() === "")) {
          errors.push("Falta código");
        }
        const deptosCount = this.getRecipeDeptosRows(it).length;
        if (deptosCount === 0) {
          errors.push("Falta al menos un departamento vinculado");
        }
        if (errors.length > 0) {
          invalidItems.push(
            `• <strong>${it.Nombre || `Receta #${idx + 1}`}</strong>: ${errors.join(", ")}`,
          );
        }
      });

      if (invalidItems.length > 0) {
        this._showValidationErrors(invalidItems);
        return;
      }

      const confirmResult = await this.$swal({
        title: `¿Deseas ${actionText} ${itemsToCreate.length} receta(s)?`,
        text: isEdit
          ? "Los cambios se guardarán directamente en los productos seleccionados."
          : "La replicación creará cada receta en la base de datos de forma secuencial y segura.",
        icon: "question",
        allowOutsideClick: false,
        showCancelButton: true,
        confirmButtonColor: this.themeHexColor,
        cancelButtonColor: "#a9a5a5",
        confirmButtonText: `Sí, ${actionText}`,
        cancelButtonText: "Regresar",
      });

      if (!confirmResult.value) return;

      const payloads = itemsToCreate.map((item) => this.preparePayload(item));
      this.loadingExecution = true;
      this.executionProgress = {
        current: 0,
        total: payloads.length,
        currentName: "",
        success: 0,
        failed: 0,
      };

      itemsToCreate.forEach((it) => {
        this.$set(it, "_status", "pending");
        this.$set(it, "_error", null);
      });

      this._showProgressDialog(actionTitle, payloads.length);

      const failedCalls = [];
      const cloudFunctionRoute = isEdit
        ? "PDV-Productos-updateProducto"
        : "PDV-Productos-createProducto";

      try {
        for (let i = 0; i < payloads.length; i++) {
          const item = itemsToCreate[i];
          const payload = payloads[i];
          const itemName = item.Nombre || `Receta #${i + 1}`;

          this.executionProgress.current = i + 1;
          this.executionProgress.currentName = itemName;
          this.$set(item, "_status", "running");

          this._updateProgressUI(i, payloads.length, itemName, "running");

          try {
            const res = await this.$store.getters.getFirebaseApp.fba.call(
              cloudFunctionRoute,
              payload,
            );

            const isOk =
              res &&
              res.data &&
              (res.data.ok === true ||
                res.data.ok === 1 ||
                res.data.success === true);

            if (isOk) {
              this.executionProgress.success++;
              this.$set(item, "_status", "success");

              const targetId =
                (res.data && res.data.id) ||
                (res.data && res.data.data
                  ? res.data.data.id
                  : payload.id || payload.Id);
              this.$pgsHelper.writeLog({
                Evento: isEdit ? "PRODUCTO_EDIT" : "PRODUCTO_CREATE",
                Mensaje: isEdit
                  ? "Producto editado por edición masiva"
                  : "Producto creado por replicación masiva",
                reference: {
                  id: targetId,
                  tp: "AYB-Producto",
                  al: "RECETA AYB",
                },
                data: payload,
              });
            } else {
              const errorMsg =
                (res && res.data && (res.data.error || res.data.message)) ||
                "Respuesta sin confirmación ok del servidor";
              this.executionProgress.failed++;
              this.$set(item, "_status", "failed");
              this.$set(item, "_error", errorMsg);
              failedCalls.push({ status: "fulfilled", value: res });
            }
          } catch (error) {
            const errorMsg =
              error && (error.message || error.error)
                ? error.message || error.error
                : "Error de conexión o servidor";
            this.executionProgress.failed++;
            this.$set(item, "_status", "failed");
            this.$set(item, "_error", errorMsg);
            failedCalls.push({ status: "rejected", reason: error });
          }

          this._updateProgressUI(
            i + 1,
            payloads.length,
            itemName,
            item._status,
          );
        }
      } catch (loopError) {
        console.error("Error durante el ciclo de operación:", loopError);
      } finally {
        this.loadingExecution = false;
      }

      const successCount = this.executionProgress.success;
      const failedCount = this.executionProgress.failed;

      await this._showResultDialog(
        actionTitle,
        isEdit,
        successCount,
        payloads.length,
        failedCount,
        failedCalls,
      );

      this.closeModal();
      this.$emit("success", { successCount, failedCount });
    },
  },
};
</script>

<style scoped>
.interactive-edit-table >>> th {
  font-weight: 700 !important;
  color: #334155 !important;
  font-size: 0.78rem !important;
  text-transform: uppercase;
  background-color: #f1f5f9 !important;
  white-space: nowrap;
}
.interactive-edit-table >>> tr.row-original-product td {
  border-top: 2px solid #e2e8f0 !important;
}
.interactive-edit-table >>> tr.row-nuevo-product td {
  border-bottom: 2px solid #cbd5e1 !important;
}
.interactive-edit-table >>> td {
  padding: 4px 8px !important;
  font-size: 0.82rem !important;
  vertical-align: middle !important;
}
.resizable-textarea >>> textarea {
  resize: vertical;
  min-height: 38px;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
